<template>
  <div class="login">
    <van-nav-bar title="会员登录" left-arrow @click-left="$router.go(-1)" />
    <div class="container">
      <div class="title">
        <h3>手机验证码登录</h3>
        <p>未注册的手机号验证通过后将自动创建账号</p>
      </div>
      <div class="form">
        <div class="form-item">
          <input class="inp" type="text" inputmode="numeric" v-model.trim="mobile" maxlength="11" placeholder="请输入手机号">
        </div>
        <div class="form-item">
          <input class="inp" type="text" v-model.trim="picCode" maxlength="5" placeholder="请输入图形验证码">
          <img v-if="picUrl" :src="picUrl" alt="图形验证码" @click="updatePic">
        </div>
        <div class="form-item">
          <input class="inp" type="text" inputmode="numeric" v-model.trim="smsCode" maxlength="6" placeholder="请输入短信验证码" @keyup.enter="Login">
          <button @click="getMsgCode">{{ timer ? count + '秒后重新发送' : '获取验证码' }}</button>
        </div>
      </div>
      <div class="login-btn" :class="{ disabled: loading }" @click="Login">登 录</div>
      <div class="links">
        <span class="link" @click="$router.push('/register')">没有账号？立即注册</span>
        <span class="link link-other" @click="$router.push('/forgetpass')">忘记密码？</span>
      </div>

      <!-- 微信内置浏览器内显示：微信一键登录 -->
      <div class="wechat-login" v-if="isWechat">
        <div class="divider"><span>其他登录方式</span></div>
        <div class="wechat-btn" :class="{ disabled: loading }" @click="wechatLogin">
          <i class="wechat-icon"></i>
          <span>微信一键登录</span>
        </div>
      </div>
      
    </div>
  </div>
</template>

<script>
import { getPicCodeApi, getMsgCodeApi, SmsLoginApi, getWechatLoginUrlApi } from '@/api/login'
import { Toast } from 'vant'

export default {
  name: 'Login',
  data () {
    return {
      // 手机号
      mobile: '',
      // 图形验证码
      picCode: '',
      // 短信验证码
      smsCode: '',
      // 图形验证码图片 base64
      picUrl: '',
      // 图形验证码标识
      picKey: '',
      // 倒计时定时器
      timer: null,
      // 倒计时秒数
      count: 60,
      loading: false,
      // 是否在微信内置浏览器中打开（仅在微信内展示微信登录入口）
      isWechat: /micromessenger/i.test(navigator.userAgent)
    }
  },
  created () {
    this.updatePic()
  },
  beforeDestroy () {
    this.clearTimer()
  },
  methods: {
    clearTimer () {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },
    startCountdown () {
      this.clearTimer()
      this.count = 60
      this.timer = setInterval(() => {
        this.count--
        if (this.count <= 0) {
          this.clearTimer()
        }
      }, 1000)
    },
    // 获取图形验证码
    async updatePic () {
      try {
        const { data: { img, key } } = await getPicCodeApi()
        this.picUrl = img
        this.picKey = key
      } catch (e) {
        console.error(e)
      }
    },
    // 获取短信验证码（未注册的手机号也可以获取，用于登录时自动注册）
    async getMsgCode () {
      if (this.timer) return
      if (!/^1[3-9]\d{9}$/.test(this.mobile)) {
        Toast('请输入正确的手机号')
        return
      }
      if (this.picCode.length < 4) {
        Toast('请输入图形验证码')
        return
      }
      try {
        await getMsgCodeApi(this.picCode, this.picKey, this.mobile)
        // 短信由后端调用阿里云真实下发，验证码不会返回给前端
        Toast('验证码已发送，请注意查收短信')
        this.startCountdown()
      } catch (err) {
        // 具体错误（如「获取验证码频繁」）已由请求拦截器弹出提示
        this.updatePic()
      }
    },
    // 手机号验证码登录：手机号未注册时后端会自动注册并登录
    async Login () {
      if (!/^1[3-9]\d{9}$/.test(this.mobile)) {
        Toast('请输入正确的手机号')
        return
      }
      if (!this.smsCode) {
        Toast('请输入短信验证码')
        return
      }
      if (this.loading) return
      this.loading = true
      try {
        const { data, message } = await SmsLoginApi(this.mobile, this.smsCode)
        // 将用户信息保存到 vuex 中（含 token）
        this.$store.commit('user/setUserInfo', data)
        this.clearTimer()
        // 后端返回 is_new_user=1 表示本次登录自动注册了新账号
        this.loginSuccess(data && data.is_new_user ? '登录成功，已自动创建账号' : (message || '登录成功'))
      } catch (err) {
        const msg = typeof err === 'string' ? err : (err && err.message) || '登录失败，请稍后重试'
        Toast(msg)
        this.updatePic()
      } finally {
        this.loading = false
      }
    },
    // 登录成功后的统一处理
    loginSuccess (msg) {
      Toast(msg || '登录成功')
      setTimeout(() => {
        // 支持从受保护页面跳转登录（?redirect=xxx）后回跳
        const redirect = this.$route.query.redirect
        if (redirect) {
          this.$router.replace(redirect)
          return
        }
        // 有历史记录则返回上一页，否则回首页
        if (window.history.length > 1) {
          this.$router.go(-1)
        } else {
          this.$router.replace('/home')
        }
      }, 800)
    },
    // 微信一键登录：获取微信授权地址并跳转
    async wechatLogin () {
      if (this.loading) return
      this.loading = true
      try {
        // 传入当前页面需要的回跳地址（如从受保护页跳转来登录）
        const redirect = this.$route.query.redirect || ''
        const { data } = await getWechatLoginUrlApi(redirect)
        if (!data || !data.url) {
          throw new Error('获取微信授权地址失败')
        }
        // 授权完成后微信会回跳到 /wechatauth?code=xxx&state=xxx
        window.location.href = data.url
      } catch (err) {
        Toast(typeof err === 'string' ? err : '获取微信授权地址失败，请稍后重试')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="less" scoped>
.container {
  padding: 60px 29px;

  .title {
    margin-bottom: 30px;

    h3 {
      font-size: 26px;
      font-weight: 400;
    }

    p {
      line-height: 40px;
      font-size: 14px;
      color: #b8b8b8;
    }
  }

  .form-item {
    border-bottom: 1px solid #f3f1f2;
    padding: 8px;
    margin-bottom: 14px;
    display: flex;
    align-items: center;

    .inp {
      display: block;
      border: none;
      outline: none;
      height: 32px;
      font-size: 14px;
      flex: 1;
    }

    img {
      width: 94px;
      height: 31px;
    }

    button {
      height: 31px;
      border: none;
      font-size: 13px;
      color: #cea26a;
      background-color: transparent;
      padding-right: 9px;
    }
  }

  .login-btn {
    width: 100%;
    height: 42px;
    margin-top: 39px;
    background: linear-gradient(90deg, #ecb53c, #ff9211);
    color: #fff;
    border-radius: 39px;
    box-shadow: 0 10px 20px 0 rgba(0, 0, 0, 0.1);
    letter-spacing: 2px;
    display: flex;
    justify-content: center;
    align-items: center;

    &.disabled {
      opacity: 0.6;
    }
  }

  .links {
    margin-top: 24px;
    text-align: center;

    .link {
      font-size: 14px;
      color: #ff6b35;
    }

    // 「忘记密码？」与注册链接同一行，留出间距
    .link-other {
      margin-left: 18px;
    }
  }

  .wechat-login {
    margin-top: 44px;

    .divider {
      display: flex;
      align-items: center;
      font-size: 12px;
      color: #b8b8b8;

      &::before,
      &::after {
        content: '';
        flex: 1;
        height: 1px;
        background: #f3f1f2;
      }

      span {
        padding: 0 12px;
      }
    }

    .wechat-btn {
      margin-top: 24px;
      height: 42px;
      border: 1px solid #07c160;
      border-radius: 39px;
      color: #07c160;
      font-size: 15px;
      letter-spacing: 1px;
      display: flex;
      justify-content: center;
      align-items: center;

      &.disabled {
        opacity: 0.6;
      }

      .wechat-icon {
        width: 22px;
        height: 22px;
        border-radius: 50%;
        margin-right: 8px;
        background: #07c160;
        position: relative;

        // 微信气泡图标（两个圆形气泡）
        &::before,
        &::after {
          content: '';
          position: absolute;
          border-radius: 50%;
          background: #fff;
        }

        &::before {
          width: 8px;
          height: 8px;
          left: 4px;
          top: 5px;
        }

        &::after {
          width: 8px;
          height: 8px;
          right: 4px;
          bottom: 4px;
        }
      }
    }
  }
}
</style>